import { HttpStatusCode } from "axios";
import type { i18n } from "i18next";

import {
  I18N_DEFAULT_NAMESPACE,
  I18N_PUBLIC_BUNDLE_PATH_SEGMENT,
  I18N_PUBLIC_NAMESPACE,
} from "../constants";
import { refreshAccessToken } from "../helpers/auth/token-refresh.helpers";
import { clearCompanyCustomTranslations } from "./i18n-custom-translations.helpers";
import { clearI18nCache } from "./i18n.cache";

// Set by enableFullTranslations, cleared on logout. The full catalogue is the same for every user, so it is
// accepted whenever some session is active and refused while none is.
let activeSessionId: number | undefined;
let lastSessionId = 0;
// Every caller awaits the same load, so concurrent enable calls share one in-flight request.
let pendingFullCatalogueLoad: Promise<void> | undefined;

/**
 * The public namespace is the pre-login file. Every other namespace is the full catalogue, which the
 * backend serves only to a session.
 */
export const getI18nLoadPath = ({
  apiBasePath,
  namespace,
}: {
  apiBasePath: string;
  namespace: string | undefined;
}): string =>
  namespace === I18N_PUBLIC_NAMESPACE
    ? `${apiBasePath}${I18N_PUBLIC_BUNDLE_PATH_SEGMENT}{{lng}}`
    : `${apiBasePath}/i18n/{{lng}}`;

/**
 * The i18next HTTP backend's fetch. The full catalogue goes with the session cookie, and an expired access
 * token gets one refresh and retry, as axios calls do; a 4xx would otherwise mark the language failed. The
 * public file needs no session, so it goes without credentials.
 */
export const fetchI18nBundle = async ({
  url,
  init,
}: {
  url: string;
  init: RequestInit;
}): Promise<Response> => {
  if (url.includes(I18N_PUBLIC_BUNDLE_PATH_SEGMENT)) {
    return fetch(url, init);
  }

  if (activeSessionId === undefined) {
    return new Response(null, { status: HttpStatusCode.Unauthorized });
  }

  const fullCatalogueInit: RequestInit = { ...init, credentials: "include" };
  const response = await fetch(url, fullCatalogueInit);

  if (
    response.status !== HttpStatusCode.Unauthorized ||
    activeSessionId === undefined
  ) {
    return response;
  }

  await refreshAccessToken();
  return fetch(url, fullCatalogueInit);
};

/**
 * The i18next HTTP backend's parser. It runs once the body is downloaded, and i18next stores the result and
 * the cache copies it synchronously after it, so this is the last point where a full catalogue arriving after
 * logout can be stopped. Throwing is the backend's way to fail a load.
 */
export const parseI18nBundle = ({
  data,
  namespaces,
}: {
  data: string;
  namespaces: string | string[] | undefined;
}): Record<string, unknown> => {
  const loadedNamespaces =
    typeof namespaces === "string" ? [namespaces] : (namespaces ?? []);

  if (
    activeSessionId === undefined &&
    loadedNamespaces.includes(I18N_DEFAULT_NAMESPACE)
  ) {
    throw new Error(
      "The session ended before the translation catalogue arrived",
    );
  }

  return JSON.parse(data) as Record<string, unknown>;
};

/**
 * i18next records a failed load in its backend connector and never requests it again, `reloadResources`
 * included. Forgetting the full catalogue's failures lets the next load ask for it: a load refused at logout,
 * or one that failed mid-session, would otherwise leave the next login without it until a page reload.
 */
const forgetFailedFullCatalogueLoads = (i18nInstance: i18n): void => {
  // `services.backendConnector` is typed `any` by i18next; `state` maps "lng|ns" to -1 on a failed load.
  const { state } = (i18nInstance.services.backendConnector ?? {}) as {
    state?: Record<string, number>;
  };

  Object.keys(state ?? {})
    .filter(
      name =>
        name.endsWith(`|${I18N_DEFAULT_NAMESPACE}`) && (state?.[name] ?? 0) < 0,
    )
    .forEach(name => Reflect.deleteProperty(state ?? {}, name));
};

/** Whether the full catalogue is in memory for every one of `languages`. */
export const isFullCatalogueLoaded = ({
  i18nInstance,
  languages,
}: {
  i18nInstance: i18n;
  languages: readonly string[];
}): boolean =>
  languages.every(language =>
    i18nInstance.hasResourceBundle(language, I18N_DEFAULT_NAMESPACE),
  );

const loadFullCatalogue = async ({
  i18nInstance,
  sessionId,
}: {
  i18nInstance: i18n;
  sessionId: number;
}): Promise<void> => {
  forgetFailedFullCatalogueLoads(i18nInstance);
  await i18nInstance.loadNamespaces(I18N_DEFAULT_NAMESPACE);

  const isCatalogueMissing = !isFullCatalogueLoaded({
    i18nInstance,
    languages: i18nInstance.languages ?? [],
  });

  // The load awaited may have been one an earlier session started and that failed; ask once more for this one.
  if (isCatalogueMissing && sessionId === activeSessionId) {
    forgetFailedFullCatalogueLoads(i18nInstance);
    await i18nInstance.loadNamespaces(I18N_DEFAULT_NAMESPACE);
  }
};

/**
 * Load the full catalogue, then point `t` at it. Callers share a load in flight, and a language whose earlier
 * load failed is requested again.
 */
export const enableFullTranslations = (i18nInstance: i18n): Promise<void> => {
  if (activeSessionId === undefined) {
    lastSessionId += 1;
    activeSessionId = lastSessionId;
  }

  if (!pendingFullCatalogueLoad) {
    const sessionId = activeSessionId;
    const load = loadFullCatalogue({ i18nInstance, sessionId })
      .then(() => {
        // Switched any earlier, every mounted useTranslation() suspends until each language has arrived.
        if (
          sessionId === activeSessionId &&
          isFullCatalogueLoaded({
            i18nInstance,
            languages: [i18nInstance.language],
          })
        ) {
          i18nInstance.setDefaultNamespace(I18N_DEFAULT_NAMESPACE);
        }
      })
      .finally(() => {
        if (pendingFullCatalogueLoad === load) {
          pendingFullCatalogueLoad = undefined;
        }
      });
    pendingFullCatalogueLoad = load;
  }

  return pendingFullCatalogueLoad;
};

/** Back to the pre-login file: the full catalogue leaves memory and the localStorage cache. */
export const disableFullTranslations = (i18nInstance: i18n): void => {
  const { ns } = i18nInstance.options;
  const namespaces = typeof ns === "string" ? [ns] : (ns ?? []);

  activeSessionId = undefined;
  pendingFullCatalogueLoad = undefined;
  clearCompanyCustomTranslations(i18nInstance);
  i18nInstance.setDefaultNamespace(I18N_PUBLIC_NAMESPACE);
  // loadNamespaces registered it; left in place, a language switch would request it without a session.
  i18nInstance.options.ns = namespaces.filter(
    namespace => namespace !== I18N_DEFAULT_NAMESPACE,
  );
  Object.keys(i18nInstance.store?.data ?? {}).forEach(language =>
    i18nInstance.removeResourceBundle(language, I18N_DEFAULT_NAMESPACE),
  );
  clearI18nCache(I18N_DEFAULT_NAMESPACE);
};
